
import React from "react";
import {
  MapPin,
  Share2,
  ChevronRight,
  Building2,
  Layers,
  Trees,
  Landmark,
  GraduationCap,
  HeartPulse,
  Briefcase,
  ShoppingBag,
  Sparkles,
  CalendarCheck,
  Home,
  BadgeCheck,
  Car,
  Ruler,
  Navigation,
  Database,
  Plane,
  TrainFront,
  Store,
  Hospital,
  School,
  Globe2,
  TrendingUp,
  Users,
  Wifi,
} from "lucide-react";

import Footer from "../../HomePage/Footer";
import Navbar from "../../HomePage/Navbar";

/* ============================================================
   PROJECT IMAGES
============================================================ */

/* ============================================================
   PROJECT IMAGES — ONLINE
============================================================ */

const hero =
  "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1600&q=85";

const masterplan =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=85";

const lifestyle =
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1600&q=85";

const locationMap =
  "https://images.unsplash.com/photo-1524666041070-9f8763d5f2f7?auto=format&fit=crop&w=1600&q=85";  



/* ============================================================
   PROJECT CONSTANTS
============================================================ */

const WHATSAPP_NUMBER = "919739580142";

const MAPS_URL =
  "https://maps.app.goo.gl/dwWNkRhi43Wo37we6";

const PROJECT_NAME = "Brigade Granada";

const WHATSAPP_MESSAGE = `Hi, I'm interested in Brigade Granada.

📍 Location: ${MAPS_URL}

🏢 40+ Acres Township
🏙 55-Floor Residential Towers
🛍 Premium Retail & Shopping
🏥 Hospital Within Township
🏫 School Within Township
🌳 Landscaped Green Spaces
🏊 World-Class Clubhouse & Amenities

Configurations:
• 1 BHK – 600–700 Sq.Ft. – ₹1 Cr onwards
• 2 BHK Regular – 1000–1100 Sq.Ft. – ₹1.5 Cr onwards
• 2 BHK Large – 1200–1300 Sq.Ft. – ₹1.75 Cr onwards
• 3 BHK + 2T – 1400–1500 Sq.Ft. – ₹2 Cr onwards
• 3 BHK + 3T – 1550–1650 Sq.Ft. – ₹2.25 Cr onwards
• 3 BHK Large – 1800–2000 Sq.Ft. – ₹2.6 Cr onwards
• 3 BHK + Study – 2100–2200 Sq.Ft. – ₹2.9 Cr onwards
• 4 BHK – 2500–2800 Sq.Ft. – ₹3.6 Cr onwards

Indicative Price: ₹14,000/Sq.Ft.

Please share the latest configuration-wise price list, floor plans and EOI details.`;

function buildWhatsAppLink() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;
}

/* ============================================================
   REUSABLE COMPONENTS
============================================================ */

function Frame({
  src,
  alt,
  ratio = "aspect-[4/5]",
  className = "",
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-gray-100 ${ratio} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        onError={(e) => {
          e.currentTarget.style.display = "none";

          if (e.currentTarget.nextSibling) {
            e.currentTarget.nextSibling.style.display = "flex";
          }
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div
        style={{ display: "none" }}
        className="absolute inset-0 items-center justify-center px-4 text-center text-xs font-medium uppercase tracking-wide text-gray-400"
      >
        {alt}
      </div>
    </div>
  );
}

function FloatingBadge({
  className = "",
  children,
}) {
  return (
    <div
      className={`absolute rounded-2xl bg-white px-5 py-3 shadow-xl shadow-gray-300/40 ${className}`}
    >
      {children}
    </div>
  );
}

function ChecklistItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">
        <Icon
          className="h-5 w-5 text-red-600"
          strokeWidth={2}
        />
      </div>

      <span className="text-sm font-semibold text-gray-800 sm:text-base">
        {children}
      </span>
    </div>
  );
}

function JumpPill({
  icon: Icon,
  label,
  target,
}) {
  return (
    <a
      href={target}
      className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:border-red-200 hover:text-red-600"
    >
      <Icon
        className="h-4 w-4"
        strokeWidth={2}
      />

      {label}

      <ChevronRight className="h-3.5 w-3.5" />
    </a>
  );
}

function StatPill({
  value,
  label,
}) {
  return (
    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center">
      <p className="text-2xl font-extrabold text-red-600">
        {value}
      </p>

      <p className="mt-0.5 text-xs font-medium text-gray-500">
        {label}
      </p>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">

      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
        <Icon
          className="h-5 w-5 text-red-600"
          strokeWidth={1.8}
        />
      </div>

      <h4 className="mt-4 text-base font-extrabold text-gray-900">
        {title}
      </h4>

      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {description}
      </p>

    </div>
  );
}

function ConfigurationCard({
  type,
  area,
  price,
}) {
  return (
    <div className="group rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">

      <div className="flex items-center justify-between">

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
          <Home
            className="h-5 w-5 text-red-600"
            strokeWidth={1.8}
          />
        </div>

        <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-600">
          EOI Open
        </span>

      </div>

      <p className="mt-5 text-sm font-extrabold uppercase tracking-wide text-red-600">
        {type}
      </p>

      <p className="mt-2 text-2xl font-extrabold text-gray-900">
        {price}
      </p>

      <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">

        <Ruler className="h-4 w-4 text-red-500" />

        {area}

      </div>

      <p className="mt-2 text-xs text-gray-400">
        Approx. area · Indicative pricing
      </p>

    </div>
  );
}

function USPItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-start gap-3">

      <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">

        <Icon
          className="h-4 w-4 text-red-600"
          strokeWidth={2}
        />

      </div>

      <span className="text-sm leading-relaxed text-gray-700">
        {children}
      </span>

    </div>
  );
}

function ConnectivityGroup({
  icon: Icon,
  title,
  rows,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">

      <div className="flex items-center gap-2.5">

        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50">

          <Icon
            className="h-5 w-5 text-red-600"
            strokeWidth={2}
          />

        </div>

        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900">
          {title}
        </h4>

      </div>

      <ul className="mt-4 space-y-3">

        {rows.map((row) => (

          <li
            key={row.name}
            className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-2.5"
          >

            <span className="text-sm font-medium text-gray-700">
              {row.name}
            </span>

            <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-bold text-red-500">
              {row.distance}
            </span>

          </li>

        ))}

      </ul>

    </div>
  );
}

/* ============================================================
   PROJECT DATA
============================================================ */

const PROJECT_STATS = [
  {
    value: "40+",
    label: "Acres",
  },
  {
    value: "55",
    label: "Floors",
  },
  {
    value: "Township",
    label: "Mixed-Use",
  },
  {
    value: "EOI",
    label: "Open Now",
  },
];

const CONFIGURATIONS = [
  {
    type: "1 BHK",
    area: "600 – 700 Sq.Ft.",
    price: "₹1 Cr onwards",
  },

  {
    type: "2 BHK Regular",
    area: "1,000 – 1,100 Sq.Ft.",
    price: "₹1.5 Cr onwards",
  },

  {
    type: "2 BHK Large",
    area: "1,200 – 1,300 Sq.Ft.",
    price: "₹1.75 Cr onwards",
  },

  {
    type: "3 BHK + 2T",
    area: "1,400 – 1,500 Sq.Ft.",
    price: "₹2 Cr onwards",
  },

  {
    type: "3 BHK + 3T",
    area: "1,550 – 1,650 Sq.Ft.",
    price: "₹2.25 Cr onwards",
  },

  {
    type: "3 BHK Large",
    area: "1,800 – 2,000 Sq.Ft.",
    price: "₹2.6 Cr onwards",
  },

  {
    type: "3 BHK + Study",
    area: "2,100 – 2,200 Sq.Ft.",
    price: "₹2.9 Cr onwards",
  },

  {
    type: "4 BHK",
    area: "2,500 – 2,800 Sq.Ft.",
    price: "₹3.6 Cr onwards",
  },
];

const TOWNSHIP_FEATURES = [
  {
    icon: Building2,
    title: "55-Floor Residential Towers",
    description:
      "High-rise residential towers forming the vertical residential core of the 40+ acre township.",
  },

  {
    icon: ShoppingBag,
    title: "Premium Retail",
    description:
      "Dedicated premium retail and shopping spaces integrated within the township.",
  },

  {
    icon: HeartPulse,
    title: "Hospital Within Township",
    description:
      "Healthcare infrastructure planned within the township for everyday convenience.",
  },

  {
    icon: School,
    title: "School Within Township",
    description:
      "Educational infrastructure planned within the integrated development.",
  },

  {
    icon: Trees,
    title: "Landscaped Green Spaces",
    description:
      "Thoughtfully planned landscaped areas designed to create a greener community environment.",
  },

  {
    icon: Sparkles,
    title: "World-Class Clubhouse",
    description:
      "Premium clubhouse and lifestyle amenities planned for residents.",
  },
];

const WHY_INVEST = [
  {
    icon: Home,
    title: "Live, Work & Relax",
    description:
      "An integrated township designed to bring residential, retail, education, healthcare and lifestyle needs together.",
  },

  {
    icon: BadgeCheck,
    title: "Brigade Brand",
    description:
      "A project positioned under the Brigade Group brand and its established real estate portfolio.",
  },

  {
    icon: TrendingUp,
    title: "Infrastructure Growth",
    description:
      "East Bengaluru continues to see infrastructure and employment-led development.",
  },

  {
    icon: Users,
    title: "Employment-Led Demand",
    description:
      "Technology companies, startups, R&D centres and GCCs contribute to continued residential demand in Bengaluru.",
  },

  {
    icon: Plane,
    title: "Airport & City Access",
    description:
      "Connectivity to Kempegowda International Airport and major employment hubs supports the eastern corridor.",
  },

  {
    icon: Database,
    title: "Digital Infrastructure",
    description:
      "The region is seeing increasing interest in data centres, cloud infrastructure and technology ecosystems.",
  },
];

const CONNECTIVITY = [
  {
    icon: Briefcase,
    title: "Employment",
    rows: [
      {
        name: "Whitefield",
        distance: "Nearby",
      },
      {
        name: "ITPL",
        distance: "Nearby",
      },
      {
        name: "KR Puram",
        distance: "Connected",
      },
      {
        name: "Outer Ring Road",
        distance: "Connected",
      },
    ],
  },

  {
    icon: GraduationCap,
    title: "Education",
    rows: [
      {
        name: "Schools",
        distance: "Nearby",
      },
      {
        name: "Colleges",
        distance: "Easy access",
      },
      {
        name: "International Schools",
        distance: "Connected",
      },
    ],
  },

  {
    icon: HeartPulse,
    title: "Healthcare",
    rows: [
      {
        name: "Township Hospital",
        distance: "Within",
      },
      {
        name: "Speciality Hospitals",
        distance: "Nearby",
      },
      {
        name: "Clinics",
        distance: "Nearby",
      },
    ],
  },

  {
    icon: ShoppingBag,
    title: "Lifestyle",
    rows: [
      {
        name: "Retail & Shopping",
        distance: "Within",
      },
      {
        name: "Malls",
        distance: "Nearby",
      },
      {
        name: "Restaurants & Leisure",
        distance: "Connected",
      },
    ],
  },
];

const EOI_BENEFITS = [
  {
    icon: BadgeCheck,
    title: "Priority Unit Selection",
    description:
      "Get priority access to available inventory during the EOI process.",
  },

  {
    icon: Home,
    title: "Preferred Inventory Access",
    description:
      "Opportunity to explore preferred inventory before wider availability.",
  },

  {
    icon: Navigation,
    title: "Better Floor & View Choice",
    description:
      "Early participation may provide access to preferred floor and view options.",
  },

  {
    icon: CalendarCheck,
    title: "Early Entry Advantage",
    description:
      "Participate early in the project launch process.",
  },
];

/* ============================================================
   PAGE
============================================================ */

export default function BrigadeGranada() {
  return (
    <div className="bg-white text-gray-900">

      <Navbar />

      {/* ======================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden bg-gradient-to-b from-red-50/80 via-red-50/20 to-white px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          {/* LEFT */}

          <div>

            <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-sm font-bold text-red-600">

              <Sparkles className="h-4 w-4" />

              EOI Open Now

            </div>

            <p className="mt-6 text-sm font-bold uppercase tracking-wider text-red-600">
              Brigade Group · East Bengaluru
            </p>

            <h1 className="mt-3 text-5xl font-extrabold leading-[1.05] text-gray-900 sm:text-6xl">

              Brigade{" "}

              <span className="text-red-600">
                Granada
              </span>

              <br />

              Seegahalli Road

            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg">

              A landmark mixed-use township on Seegahalli Road,
              East Bengaluru, bringing premium residences, retail,
              healthcare, education, landscaped spaces and lifestyle
              amenities together across 40+ acres.

            </p>

            {/* ACTIONS */}

            <div className="mt-8 flex flex-wrap gap-3">

              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700"
              >
                Contact & Enquire
              </a>

              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
              >

                <Share2 className="h-4 w-4" />

                Enquire on WhatsApp

              </a>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-6 py-3.5 text-sm font-bold text-red-600 transition hover:bg-red-50"
              >

                <MapPin className="h-4 w-4" />

                View Location

              </a>

            </div>

            {/* CHECKLIST */}

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">

              <ChecklistItem icon={Building2}>
                40+ Acre Township
              </ChecklistItem>

              <ChecklistItem icon={Layers}>
                55-Floor Towers
              </ChecklistItem>

              <ChecklistItem icon={ShoppingBag}>
                Premium Retail
              </ChecklistItem>

              <ChecklistItem icon={Trees}>
                Landscaped Green Spaces
              </ChecklistItem>

            </div>

          </div>

          {/* RIGHT */}

          <div className="relative">

            <Frame
              src={hero}
              alt="Brigade Granada township"
              ratio="aspect-[4/3]"
            />

            <FloatingBadge className="-left-3 top-6 sm:-left-6">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Starting From
              </p>

              <p className="mt-1 text-xl font-extrabold text-red-600">
                ₹1 Cr*
              </p>

              <p className="text-xs text-gray-500">
                1 BHK
              </p>

            </FloatingBadge>

            <FloatingBadge className="-bottom-5 -right-3 sm:-right-6">

              <p className="text-xl font-extrabold text-gray-900">
                40+
              </p>

              <p className="text-xs text-gray-500">
                Acres Township
              </p>

            </FloatingBadge>

          </div>

        </div>

      </section>

      {/* ======================================================
          QUICK STATS
      ====================================================== */}

      <section className="px-5 sm:px-10">

        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-4">

          {PROJECT_STATS.map((stat) => (
            <StatPill
              key={stat.label}
              value={stat.value}
              label={stat.label}
            />
          ))}

        </div>

      </section>

      {/* ======================================================
          PROJECT OVERVIEW
      ====================================================== */}

      <section
        id="overview"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          <Frame
            src={masterplan}
            alt="Brigade Granada masterplan"
            ratio="aspect-[4/3]"
          />

          <div>

            <p className="text-sm font-bold uppercase tracking-wide text-red-600">
              Project Overview
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              A complete destination for modern urban living.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

              Brigade Granada is envisioned as a landmark mixed-use
              township in East Bengaluru. The development combines
              residential towers with retail, healthcare, education,
              landscaped spaces and premium community amenities.

            </p>

            <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">

              Its location on Seegahalli Road provides connectivity
              towards Whitefield, Kadugodi, ITPL and KR Puram while
              placing everyday conveniences within the larger township
              ecosystem.

            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Township
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  40+ Acres
                </p>

              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Towers
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  55 Floors
                </p>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          TOWNSHIP FEATURES
      ====================================================== */}

      <section
        id="features"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Township Highlights
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Everything you need, within the destination.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-gray-600">
            A mixed-use township concept designed to combine
            residential living with everyday services, green spaces
            and community amenities.
          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {TOWNSHIP_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                {...feature}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          CONFIGURATIONS & PRICING
      ====================================================== */}

      <section
        id="configurations"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Configurations & Pricing
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Homes designed for different lifestyles.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-gray-600">
            Choose from 1 BHK through spacious 4 BHK configurations
            across different size ranges.
          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

            {CONFIGURATIONS.map((configuration) => (
              <ConfigurationCard
                key={configuration.type}
                {...configuration}
              />
            ))}

          </div>

          {/* PRICE NOTE */}

          <div className="mt-8 rounded-2xl border border-yellow-200 bg-yellow-50 px-5 py-4 text-sm leading-relaxed text-yellow-800">

            <strong>Pricing note:</strong>{" "}
            The prices shown are indicative starting prices.
            The stated base price is ₹14,000/sq.ft. Prices exclude
            applicable FRC and PLC charges. Please confirm the latest
            official price sheet, availability and applicable charges
            before making any financial commitment.

          </div>

        </div>

      </section>

      {/* ======================================================
          WHY THIS TOWNSHIP
      ====================================================== */}

      <section
        id="why"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Key Themes
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Why the East Bengaluru corridor is attracting attention.
          </h2>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-600">
            The following factors are part of the project's stated
            investment and market narrative. Actual property
            performance can vary and should be evaluated independently.
          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {WHY_INVEST.map((item) => (
              <FeatureCard
                key={item.title}
                {...item}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          EMPLOYMENT LED DEMAND
      ====================================================== */}

      <section className="px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          <div>

            <p className="text-sm font-bold uppercase tracking-wide text-red-600">
              Employment-Led Demand
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Bengaluru's technology ecosystem continues to shape residential demand.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">

              Bengaluru's technology, startup, R&D and Global Capability
              Centre ecosystem attracts professionals from across India.
              This creates a large working-age population base across
              established employment corridors.

            </p>

            <div className="mt-8 space-y-4">

              <USPItem icon={Briefcase}>
                Technology companies and startups
              </USPItem>

              <USPItem icon={Globe2}>
                Global Capability Centres and R&D centres
              </USPItem>

              <USPItem icon={Users}>
                Continued migration of working-age professionals
              </USPItem>

              <USPItem icon={Wifi}>
                Expanding technology and digital infrastructure
              </USPItem>

            </div>

          </div>

          <Frame
            src={lifestyle}
            alt="Bengaluru technology and urban lifestyle"
            ratio="aspect-[4/3]"
          />

        </div>

      </section>

      {/* ======================================================
          CONNECTIVITY
      ====================================================== */}

      <section
        id="connectivity"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Connectivity
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Connected to Bengaluru's eastern employment hubs.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-gray-600">
            Seegahalli Road provides access towards Whitefield,
            Kadugodi, ITPL and KR Puram, connecting residents to
            established employment, education, healthcare and
            lifestyle destinations.
          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">

            {CONNECTIVITY.map((group) => (
              <ConnectivityGroup
                key={group.title}
                {...group}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          LOCATION
      ====================================================== */}

      <section
        id="location"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-red-600">
                Prime Location
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Seegahalli Road, East Bengaluru.
              </h2>

              <p className="mt-4 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base">

                The project is positioned in the eastern Bengaluru
                growth corridor with access towards Whitefield,
                Kadugodi, ITPL and KR Puram.

              </p>

              <div className="mt-7 space-y-3">

                <div className="flex items-center gap-3">

                  <MapPin className="h-5 w-5 text-red-600" />

                  <span className="text-sm font-semibold text-gray-700">
                    Seegahalli Road, East Bengaluru
                  </span>

                </div>

                <div className="flex items-center gap-3">

                  <Building2 className="h-5 w-5 text-red-600" />

                  <span className="text-sm font-semibold text-gray-700">
                    Connected to Whitefield & ITPL
                  </span>

                </div>

                <div className="flex items-center gap-3">

                  <TrainFront className="h-5 w-5 text-red-600" />

                  <span className="text-sm font-semibold text-gray-700">
                    Access towards KR Puram & Kadugodi
                  </span>

                </div>

              </div>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-red-200 transition hover:bg-red-700"
              >

                <Navigation className="h-4 w-4" />

                Open in Google Maps

              </a>

            </div>

            <Frame
              src={locationMap}
              alt="Brigade Granada Seegahalli Road location"
              ratio="aspect-[4/3]"
            />

          </div>

        </div>

      </section>

      {/* ======================================================
          INFRASTRUCTURE GROWTH
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-6 md:grid-cols-2">

            <div className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">

                <Plane className="h-6 w-6 text-red-600" />

              </div>

              <h3 className="mt-5 text-xl font-extrabold text-gray-900">
                Airport & Economic Expansion
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-gray-600">

                East Bengaluru benefits from connectivity towards
                Kempegowda International Airport and major employment
                hubs including Whitefield, ITPL and the Outer Ring Road.

              </p>

            </div>

            <div className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">

                <Database className="h-6 w-6 text-red-600" />

              </div>

              <h3 className="mt-5 text-xl font-extrabold text-gray-900">
                Digital Infrastructure Advantage
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-gray-600">

                The project material highlights the proposed data-centre
                and digital infrastructure ecosystem as a potential
                catalyst for cloud, AI compute, hyperscaler and
                technology-related employment.

              </p>

            </div>

            <div className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">

                <Home className="h-6 w-6 text-red-600" />

              </div>

              <h3 className="mt-5 text-xl font-extrabold text-gray-900">
                Evolving Homebuyer Preferences
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-gray-600">

                The project narrative identifies higher incomes,
                lifestyle changes and growing interest in integrated
                townships as factors influencing premium housing demand.

              </p>

            </div>

            <div className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">

                <TrendingUp className="h-6 w-6 text-red-600" />

              </div>

              <h3 className="mt-5 text-xl font-extrabold text-gray-900">
                Infrastructure-Led Growth
              </h3>

              <p className="mt-3 text-sm leading-relaxed text-gray-600">

                Expanding mobility and infrastructure networks are
                contributing to new development corridors across
                Bengaluru's eastern side.

              </p>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          EOI BENEFITS
      ====================================================== */}

      <section
        id="eoi"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="rounded-[32px] border border-red-100 bg-red-50 p-7 sm:p-10">

            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-red-600 shadow-sm">

                  <BadgeCheck className="h-4 w-4" />

                  EOI Open Now

                </div>

                <h2 className="mt-5 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                  Early access to the project.
                </h2>

                <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">

                  The project material describes the EOI as
                  non-bankable and highlights priority access to
                  inventory and selection preferences.

                </p>

              </div>

              <div className="grid gap-4 sm:grid-cols-2">

                {EOI_BENEFITS.map((benefit) => (

                  <div
                    key={benefit.title}
                    className="rounded-2xl bg-white p-5 shadow-sm"
                  >

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50">

                      <benefit.icon
                        className="h-5 w-5 text-red-600"
                      />

                    </div>

                    <h4 className="mt-4 text-sm font-extrabold text-gray-900">
                      {benefit.title}
                    </h4>

                    <p className="mt-2 text-xs leading-relaxed text-gray-500">
                      {benefit.description}
                    </p>

                  </div>

                ))}

              </div>

            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">

              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700"
              >
                Contact & Enquire About EOI
              </a>

              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
              >
                <Share2 className="h-4 w-4" />

                Enquire on WhatsApp

              </a>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          FINAL CTA
      ====================================================== */}

      <section className="bg-gradient-to-r from-red-600 to-red-500 px-5 py-16 text-center sm:px-10 sm:py-20">

        <Sparkles
          className="mx-auto h-7 w-7 text-white"
          strokeWidth={1.6}
        />

        <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold text-white sm:text-4xl">

          Live. Work. Relax.
          <br />
          All in One Destination.

        </h2>

        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-red-50">

          Explore Brigade Granada configurations, pricing,
          floor plans, availability and EOI details.

        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-red-600 shadow-lg transition hover:bg-red-50"
          >
            Contact & Enquire
          </a>

          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
          >
            <Share2 className="h-4 w-4" />

            WhatsApp Enquiry

          </a>

          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
          >

            <MapPin className="h-4 w-4" />

            View Location

          </a>

        </div>

        <p className="mt-5 text-xs font-semibold text-red-100">

          EOI Open · Pricing and availability subject to change

        </p>

      </section>

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <Footer />

      {/* ======================================================
          FLOATING WHATSAPP
      ====================================================== */}

      <a
        href={buildWhatsAppLink()}
        target="_blank"
        rel="noreferrer"
        aria-label="Enquire about Brigade Granada on WhatsApp"
        className="fixed bottom-6 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-xl shadow-green-300/50 transition-transform hover:scale-105 sm:bottom-8 sm:right-8"
      >

        <Share2 className="h-5 w-5" />

      </a>

    </div>
  );
}
